import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  countPrimaryGenerationBindings,
  formatBool,
  formatDate,
  purposeLabel,
  repositoryErrorMessage,
  safeBindings,
  safeConfigs,
} from './Settings/repositorySourceConfigUtils';

function PurposeBindingsCell({ bindings }) {
  if (!Array.isArray(bindings) || bindings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="repository-source-config-bindings-empty">
        No purpose bindings configured.
      </Typography>
    );
  }

  return (
    <Stack spacing={1} data-testid="repository-source-config-bindings">
      {bindings.map((binding) => {
        const isPrimaryGeneration = binding.source_purpose === 'automation_generation' && binding.is_primary;
        return (
          <Stack key={binding.id || `${binding.source_purpose}-${binding.display_name}`} spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={binding.enabled ? 'primary' : 'default'}
                variant={binding.enabled ? 'filled' : 'outlined'}
                label={purposeLabel(binding.source_purpose)}
                data-testid={`repository-source-purpose-${binding.source_purpose}`}
              />
              {!binding.enabled && (
                <Chip size="small" variant="outlined" label="Disabled" />
              )}
            </Stack>
            {isPrimaryGeneration && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Primary generation repo"
                data-testid="repository-source-primary-generation-marker"
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

function ConfigsTable({ configs }) {
  if (configs.length === 0) {
    return (
      <Alert severity="info" data-testid="repository-source-config-empty">
        No repository source configurations found.
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" data-testid="repository-source-config-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Repository</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Branches</TableCell>
            <TableCell>Credentials</TableCell>
            <TableCell>Verification</TableCell>
            <TableCell>Purpose bindings</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configs.map((config) => (
            <TableRow key={config.id}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {config.repo_owner}/{config.repo_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Provider: {config.provider || '-'}
                </Typography>
                {config.repo_url && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {config.repo_url}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={config.enabled ? 'success' : 'default'}
                  label={config.enabled ? 'Enabled' : 'Disabled'}
                  data-testid={`repository-source-config-status-${config.id}`}
                />
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">
                  Default: {config.default_branch || '-'}
                </Typography>
                <Typography variant="caption" display="block">
                  Base: {config.base_branch || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">
                  Credential configured: {formatBool(config.credential_configured)}
                </Typography>
                <Typography variant="caption" display="block">
                  Token configured: {formatBool(config.token_configured)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" display="block">
                  Required: {formatBool(config.verification_required)}
                </Typography>
                <Typography variant="caption" display="block">
                  Verified: {formatBool(config.verified)}
                </Typography>
                {config.last_verification_status && (
                  <Typography variant="caption" display="block">
                    Status: {config.last_verification_status}
                  </Typography>
                )}
                {config.last_verification_message && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {config.last_verification_message}
                  </Typography>
                )}
                {config.last_verified_at && (
                  <Typography variant="caption" display="block">
                    Last verified: {formatDate(config.last_verified_at)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <PurposeBindingsCell bindings={config.purpose_bindings} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SafetyNote() {
  return (
    <Alert severity="info" data-testid="repository-source-config-safety-note">
      Repository configuration is read-only on this page. Viewing it does not start extraction or generation.
    </Alert>
  );
}

const RepositorySourceConfigAdmin = ({ selectedProject, authenticatedUser, apiClient }) => {
  const projectId = selectedProject?.project_id;
  const isAdmin = authenticatedUser?.role === 'project_admin' || authenticatedUser?.role === 'super_admin';
  const [configs, setConfigs] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const primaryGenerationCount = useMemo(() => countPrimaryGenerationBindings(configs), [configs]);

  const loadData = useCallback(async () => {
    if (!projectId || !apiClient?.listRepositorySourceConfigs || !apiClient?.listRepositorySourcePurposeBindings) {
      setConfigs([]);
      setBindings([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [configsResponse, bindingsResponse] = await Promise.all([
        apiClient.listRepositorySourceConfigs(projectId),
        apiClient.listRepositorySourcePurposeBindings(projectId),
      ]);
      setConfigs(safeConfigs(configsResponse));
      setBindings(safeBindings(bindingsResponse));
    } catch (err) {
      setConfigs([]);
      setBindings([]);
      setError(repositoryErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, apiClient]);

  useEffect(() => {
    setConfigs([]);
    setBindings([]);
    setError(null);
  }, [projectId]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, loadData]);

  if (!projectId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid="repository-source-config-page">
        <Typography>Select a project to inspect repository source configuration.</Typography>
      </Paper>
    );
  }

  if (!isAdmin) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }} data-testid="repository-source-config-page">
        <Alert severity="warning" data-testid="repository-source-config-access-denied">
          Admin access required.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box data-testid="repository-source-config-page" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Repository Source Configuration
          </Typography>
          <Typography color="text.secondary">
            Read-only admin view for repository configs and purpose bindings for the selected project.
          </Typography>
        </Box>

        <SafetyNote />

        {loading && <CircularProgress size={24} aria-label="Loading repository source configuration" />}
        {error && <Alert severity="error" data-testid="repository-source-config-error">{error}</Alert>}

        {!loading && !error && (
          <>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Chip label={`${configs.length} repository config${configs.length === 1 ? '' : 's'}`} variant="outlined" />
                <Chip label={`${bindings.length} purpose binding${bindings.length === 1 ? '' : 's'}`} variant="outlined" />
                <Chip
                  label={`${primaryGenerationCount} primary generation marker${primaryGenerationCount === 1 ? '' : 's'}`}
                  variant="outlined"
                  data-testid="repository-source-primary-generation-count"
                />
              </Stack>
            </Paper>
            <ConfigsTable configs={configs} />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default RepositorySourceConfigAdmin;
